/* /professional/login — Phone + OTP login for verified professionals.
 *
 * Uses /api/pro/auth/* endpoints. The OTP is shown inline in DEV mode so
 * the founder can verify professionals end-to-end without an SMS provider.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Smartphone,
  ShieldCheck,
  Loader2,
  AlertTriangle,
  KeySquare,
  ArrowLeft,
  HardHat,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatErr = (detail, fallback) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const ProfessionalLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const next = params.get("next") || "/professional/dashboard";

  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devOtp, setDevOtp] = useState(null); // retained for backwards compat; always null in production
  const [infoMsg, setInfoMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    document.title = "Professional Login · Mera Naksha";
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  useEffect(() => {
    // If already authed, jump to next.
    axios.get(`${API}/pro/auth/me`).then(() => navigate(next, { replace: true })).catch(() => {});
  }, [navigate, next]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const normalisedPhone = phone.replace(/\D+/g, "");
  const phoneValid = /^[6-9]\d{9}$/.test(normalisedPhone.replace(/^91/, ""));

  const requestOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (!phoneValid) { setError("Please enter a valid 10-digit Indian mobile number."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/pro/auth/request-otp`, { phone: normalisedPhone });
      setInfoMsg(data?.message || "OTP sent to your mobile number.");
      setStep("otp");
      setCooldown(30);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail, "Could not send OTP. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setError("");
    if (!/^\d{4,8}$/.test(code.trim())) { setError("Enter the OTP we just sent."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/pro/auth/verify-otp`, { phone: normalisedPhone, code: code.trim() });
      navigate(next, { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail, "Could not verify OTP."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy text-white grid place-items-center px-4 py-10 relative overflow-hidden" data-testid="pro-login-page">
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center gap-3 mb-7">
          <div className="bg-white rounded-xl px-3 py-2 shadow-md">
            <BrandLogo size="md" asLink={false} withTagline lang="en" testId="pro-login-brand-logo" />
          </div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[11px] font-bold tracking-widest uppercase">
            <HardHat className="h-3.5 w-3.5" /> Professional Portal
          </div>
        </div>

        <form
          onSubmit={step === "phone" ? requestOtp : verifyOtp}
          className="bg-white text-foreground rounded-3xl shadow-2xl border-2 border-gold/30 overflow-hidden"
          data-testid="pro-login-form"
        >
          <div className="p-6 sm:p-8 space-y-5">
            <div>
              <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-navy">
                {step === "phone" ? "Sign in to your dashboard" : "Enter the OTP"}
              </h1>
              <p className="text-[13px] text-muted-foreground mt-1">
                {step === "phone"
                  ? "We'll send a one-time code to your registered mobile number."
                  : `Code sent to +91 ${normalisedPhone.slice(-10)}. It expires in 5 minutes.`}
              </p>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-red-50 border-2 border-red-200 px-3 py-2 text-[13px] text-red-800 font-semibold" data-testid="pro-login-error" role="alert">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {step === "phone" && (
              <>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">Mobile number</span>
                  <div className="mt-1.5 flex items-center gap-2 rounded-xl border-2 border-border bg-white focus-within:border-navy h-11 overflow-hidden">
                    <span className="pl-3 pr-1 text-[14px] font-bold text-navy tabular-nums">+91</span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      required
                      autoFocus
                      autoComplete="tel-national"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="98765 43210"
                      data-testid="pro-login-phone"
                      maxLength={14}
                      className="flex-1 h-full bg-transparent text-[15px] tabular-nums outline-none"
                    />
                  </div>
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="pro-login-request-otp"
                  className="w-full min-h-[48px] rounded-2xl bg-gold text-navy font-extrabold text-[14.5px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white shadow-[0_8px_18px_-8px_rgba(212,162,42,0.6)] disabled:opacity-60"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : (<><Smartphone className="h-4 w-4" /> Send OTP</>)}
                </button>
                <p className="text-center text-[12.5px] text-muted-foreground">
                  Not registered?{" "}
                  <Link to="/professional/register" data-testid="pro-login-cta-register" className="text-navy font-extrabold hover:text-gold-dark">
                    Apply to join
                  </Link>
                </p>
              </>
            )}

            {step === "otp" && (
              <>
                {infoMsg && (
                  <div className="rounded-xl bg-emerald-50 border-2 border-emerald-300 px-3.5 py-3 text-[12.5px] text-emerald-900 font-semibold" data-testid="pro-login-info-banner">
                    <div className="flex items-center gap-2"><KeySquare className="h-4 w-4" /><span className="uppercase tracking-wider text-[11px] font-extrabold">Check your SMS</span></div>
                    <div className="mt-1 leading-snug">{infoMsg}</div>
                  </div>
                )}
                <label className="block">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-navy/70 font-bold">One-time code</span>
                  <input
                    inputMode="numeric"
                    pattern="\d*"
                    required
                    autoFocus
                    maxLength={8}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D+/g, ""))}
                    placeholder="6-digit code"
                    data-testid="pro-login-otp"
                    className="mt-1.5 w-full h-12 px-3 rounded-xl border-2 border-border bg-white text-center text-[18px] tracking-[0.4em] tabular-nums font-extrabold outline-none focus:border-navy"
                  />
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="pro-login-verify-otp"
                  className="w-full min-h-[48px] rounded-2xl bg-navy text-gold font-extrabold text-[14.5px] flex items-center justify-center gap-2 hover:bg-navy-deep disabled:opacity-60"
                >
                  {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>) : (<><ShieldCheck className="h-4 w-4" /> Verify and continue</>)}
                </button>
                <div className="flex items-center justify-between gap-2 text-[12.5px]">
                  <button type="button" onClick={() => { setStep("phone"); setCode(""); setDevOtp(null); setError(""); }} data-testid="pro-login-back" className="inline-flex items-center gap-1.5 text-navy hover:text-gold-dark font-bold">
                    <ArrowLeft className="h-3.5 w-3.5" /> Change number
                  </button>
                  <button type="button" onClick={requestOtp} disabled={cooldown > 0 || submitting} data-testid="pro-login-resend" className="text-navy font-bold hover:text-gold-dark disabled:text-muted-foreground">
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="px-6 sm:px-8 py-3 bg-navy/[0.04] border-t border-border text-[11px] text-muted-foreground flex items-center justify-between">
            <span>© Team Mera Naksha</span>
            <span className="inline-flex items-center gap-1 font-semibold"><ShieldCheck className="h-3 w-3" /> Secure OTP login</span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfessionalLogin;
